import { useState, FormEvent, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://eventshowcase.de';

const SignupPage = () => {
  const [searchParams] = useSearchParams();
  const redirectPath = searchParams.get('redirect');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedToTaxPolicy, setAgreedToTaxPolicy] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [googleLoading, setGoogleLoading] = useState(false);

  useEffect(() => {
    const PAGE_TITLE = 'Create Free Account – Join EVENTSHOWCASE';
    const PAGE_DESC = 'Sign up for a free EVENTSHOWCASE account. Create and manage events, grow your social following, and sell digital products. No credit card required.';
    const PAGE_URL = `${SITE_URL}/signup`;

    document.title = PAGE_TITLE;

    const setMeta = (sel: string, attr: string, val: string) => {
      let el = document.querySelector(sel) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        const [a, v] = sel.replace('meta[', '').replace(']', '').split('=');
        el.setAttribute(a.trim(), v.replace(/"/g, '').trim());
        document.head.appendChild(el);
        el.setAttribute('data-page-meta', 'true');
      }
      el.setAttribute(attr, val);
    };
    setMeta('meta[name="description"]', 'content', PAGE_DESC);
    setMeta('meta[name="robots"]', 'content', 'noindex, nofollow');
    setMeta('meta[property="og:title"]', 'content', PAGE_TITLE);
    setMeta('meta[property="og:description"]', 'content', PAGE_DESC);
    setMeta('meta[property="og:url"]', 'content', PAGE_URL);
    setMeta('meta[property="og:type"]', 'content', 'website');

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Create Free Account – EVENTSHOWCASE',
      description: PAGE_DESC,
      url: PAGE_URL,
      isPartOf: { '@type': 'WebSite', name: 'EVENTSHOWCASE', url: SITE_URL },
    };

    const existing = document.getElementById('schema-signup');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.id = 'schema-signup';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('schema-signup');
      if (el) el.remove();
      document.querySelectorAll('meta[data-page-meta="true"]').forEach(e => e.remove());
    };
  }, []);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    } else if (formData.fullName.trim().length < 2) {
      newErrors.fullName = 'Name must be at least 2 characters';
    }
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Must include uppercase, lowercase, and a number';
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    if (!agreedToTerms) {
      newErrors.terms = 'You must agree to the terms';
    }
    if (!agreedToTaxPolicy) {
      newErrors.taxPolicy = 'You must acknowledge the tax responsibility';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getPasswordStrength = () => {
    const { password } = formData;
    if (!password) return { level: 0, label: '', color: '' };
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    if (score <= 2) return { level: 1, label: 'Weak', color: 'bg-red-400' };
    if (score <= 4) return { level: 2, label: 'Medium', color: 'bg-amber-400' };
    return { level: 3, label: 'Strong', color: 'bg-emerald-400' };
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErrors({});
    if (!validateForm()) return;

    const trimmedEmail = formData.email.trim();

    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email: trimmedEmail,
        password: formData.password,
        options: {
          data: { full_name: formData.fullName.trim() },
        },
      });

      if (error) {
        if (error.message.toLowerCase().includes('rate limit') || error.message.toLowerCase().includes('over_email_send_rate_limit') || error.message.toLowerCase().includes('email rate limit')) {
          setErrors({ general: 'Too many signup attempts. Please try again in a few seconds, or use a different email address.' });
        } else if (error.message.toLowerCase().includes('already registered') || error.message.toLowerCase().includes('user already registered')) {
          setErrors({ general: 'An account with this email already exists. Please log in instead.' });
        } else {
          setErrors({ general: error.message });
        }
        setIsLoading(false);
        return;
      }

      if (data?.user) {
        const isAdmin = trimmedEmail.toLowerCase() === 'cazeludegbunam@gmail.com';
        await supabase.from('profiles').upsert(
          [{
            id: data.user.id,
            email: trimmedEmail.toLowerCase(),
            full_name: formData.fullName.trim(),
            role: isAdmin ? 'admin' : 'user',
            tax_acknowledged_at: agreedToTaxPolicy ? new Date().toISOString() : null,
          }],
          { onConflict: 'id' }
        );
        setRegisteredEmail(trimmedEmail);
        setSignupSuccess(true);
      }
    } catch {
      setErrors({ general: 'An unexpected error occurred. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendEmail = async () => {
    setResendLoading(true);
    setResendMessage('');
    try {
      const { error } = await supabase.auth.resend({ type: 'signup', email: registeredEmail });
      if (error) {
        setResendMessage('Could not resend email. Please wait a moment and try again.');
      } else {
        setResendMessage('Confirmation email resent! Please check your inbox.');
      }
    } catch {
      setResendMessage('Something went wrong. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    setGoogleLoading(true);
    setErrors({});
    try {
      // Redirect to root — Supabase PKCE handles the code exchange automatically
      // This avoids bad_oauth_state errors caused by cookie loss
      const redirectTo = `${SITE_URL}/`;
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo,
          queryParams: {
            access_type: 'offline',
            prompt: 'select_account',
          },
        },
      });
      if (error) {
        setErrors({ general: error.message });
        setGoogleLoading(false);
      }
    } catch {
      setErrors({ general: 'Failed to connect to Google. Please try again.' });
      setGoogleLoading(false);
    }
  };

  const passwordStrength = getPasswordStrength();

  // Email Confirmation Screen
  if (signupSuccess) {
    return (
      <div className="min-h-screen flex">
        {/* Left Panel */}
        <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/575d7eddbe1cc67107eff36646833c35.jpg"
            alt="Event networking"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/40 to-teal-900/30"></div>
          <div className="relative z-10 flex flex-col justify-end p-12 pb-16">
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-2 h-2 rounded-full bg-teal-400"></div>
                <span className="text-teal-300 text-sm font-medium tracking-wide uppercase">Join EVENTSHOWCASE</span>
              </div>
              <h2 className="text-4xl font-bold text-white leading-tight mb-4">
                Create, share &<br />attend events
              </h2>
              <p className="text-gray-300 text-base leading-relaxed max-w-md">
                Join a community of event creators and attendees. Start hosting your own events or discover amazing experiences near you.
              </p>
            </div>
          </div>
        </div>

        {/* Right Panel - Confirmation */}
        <div className="w-full lg:w-1/2 flex items-center justify-center px-4 sm:px-6 py-8 bg-white">
          <div className="w-full max-w-[420px] text-center">
            {/* Logo */}
            <Link to="/" className="inline-flex items-center gap-2 mb-10">
              <div className="w-9 h-9 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-lg flex items-center justify-center">
                <i className="ri-calendar-event-line text-white text-lg"></i>
              </div>
              <span className="text-xl font-bold text-gray-900">EVENTSHOWCASE</span>
            </Link>

            {/* Icon */}
            <div className="w-20 h-20 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center">
                <i className="ri-mail-send-line text-teal-600 text-3xl"></i>
              </div>
            </div>

            <h1 className="text-2xl font-bold text-gray-900 mb-3">Check your email</h1>
            <p className="text-sm text-gray-500 leading-relaxed mb-2">
              We&apos;ve sent a confirmation link to:
            </p>
            <p className="text-sm font-semibold text-teal-600 bg-teal-50 border border-teal-100 rounded-lg px-4 py-2.5 inline-block mb-6">
              {registeredEmail}
            </p>

            <div className="bg-gray-50 border border-gray-100 rounded-xl p-5 mb-6 text-left space-y-3">
              {[
                { icon: 'ri-inbox-line', text: 'Open your email inbox' },
                { icon: 'ri-mail-open-line', text: 'Find the email from EVENTSHOWCASE' },
                { icon: 'ri-cursor-line', text: 'Click the confirmation link inside' },
                { icon: 'ri-checkbox-circle-line', text: 'Your account will be activated' },
              ].map((step, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-7 h-7 flex items-center justify-center rounded-full bg-teal-100 shrink-0">
                    <i className={`${step.icon} text-teal-600 text-sm`}></i>
                  </div>
                  <span className="text-sm text-gray-600">{step.text}</span>
                </div>
              ))}
            </div>

            <p className="text-xs text-gray-400 mb-4">
              Didn&apos;t receive the email? Check your spam folder or resend it.
            </p>

            {resendMessage && (
              <div className={`mb-4 p-3 rounded-lg text-xs font-medium ${resendMessage.includes('resent') ? 'bg-teal-50 text-teal-700 border border-teal-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                {resendMessage}
              </div>
            )}

            <button
              onClick={handleResendEmail}
              disabled={resendLoading}
              className="w-full py-2.5 border border-teal-500 text-teal-600 rounded-lg text-sm font-semibold hover:bg-teal-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer mb-4"
            >
              {resendLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  Resending...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-refresh-line"></i>
                  Resend Confirmation Email
                </span>
              )}
            </button>

            <Link
              to={redirectPath ? `/login?redirect=${encodeURIComponent(redirectPath)}` : '/login'}
              className="w-full py-2.5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg text-sm font-semibold hover:from-teal-600 hover:to-cyan-600 transition-all shadow-md hover:shadow-lg whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
            >
              <i className="ri-login-circle-line"></i>
              Go to Login
            </Link>

            <div className="text-center mt-6">
              <Link to="/" className="text-xs text-gray-400 hover:text-gray-600 transition-colors inline-flex items-center gap-1.5">
                <i className="ri-arrow-left-line"></i>
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Decorative - Hidden on mobile */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=A%20creative%20networking%20event%20in%20a%20modern%20venue%20with%20warm%20ambient%20lighting%2C%20people%20socializing%20and%20connecting%2C%20string%20lights%20overhead%2C%20teal%20and%20warm%20gold%20color%20palette%2C%20elegant%20indoor%20setting%20with%20plants%20and%20modern%20decor%2C%20cinematic%20photography&width=960&height=1080&seq=signup-bg-1&orientation=portrait"
          alt="Event networking"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/40 to-teal-900/30"></div>
        <div className="relative z-10 flex flex-col justify-end p-12 pb-16">
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-2 h-2 rounded-full bg-teal-400"></div>
              <span className="text-teal-300 text-sm font-medium tracking-wide uppercase">Join EVENTSHOWCASE</span>
            </div>
            <h2 className="text-4xl font-bold text-white leading-tight mb-4">
              Create, share &<br />attend events
            </h2>
            <p className="text-gray-300 text-base leading-relaxed max-w-md">
              Join a community of event creators and attendees. Start hosting your own events or discover amazing
              experiences near you.
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex -space-x-2">
              {['A', 'B', 'C', 'D'].map((letter, i) => (
                <div
                  key={letter}
                  className="w-9 h-9 rounded-full border-2 border-gray-900 flex items-center justify-center text-xs font-bold text-white"
                  style={{
                    background: ['#14b8a6', '#06b6d4', '#0d9488', '#0891b2'][i],
                  }}
                >
                  {letter}
                </div>
              ))}
            </div>
            <p className="text-sm text-gray-300">
              <strong className="text-white">50,000+</strong> people already joined
            </p>
          </div>
        </div>
      </div>

      {/* Right Panel - Form - Full width on mobile */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-4 sm:px-6 py-8 sm:py-10 bg-white">
        <div className="w-full max-w-[420px]">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 sm:gap-3 mb-8 sm:mb-10">
            <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-lg flex items-center justify-center">
              <i className="ri-calendar-event-line text-white text-lg sm:text-xl"></i>
            </div>
            <span className="text-lg sm:text-xl font-bold text-gray-900">EVENTSHOWCASE</span>
          </Link>

          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1.5">Create your account</h1>
            <p className="text-xs sm:text-sm text-gray-500">Fill in the details below to get started</p>
          </div>

          {/* General Error */}
          {errors.general && (
            <div className="mb-4 sm:mb-6 p-3 sm:p-3.5 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2 sm:gap-3">
              <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center rounded-full bg-amber-100 shrink-0 mt-0.5">
                <i className="ri-information-line text-amber-600 text-base sm:text-lg"></i>
              </div>
              <div>
                <span className="text-xs sm:text-sm text-amber-700 font-medium">{errors.general}</span>
                {errors.general.includes('already exists') && (
                  <Link
                    to="/login"
                    className="block mt-1 text-xs text-amber-600 underline hover:text-amber-800"
                  >
                    Go to Login &rarr;
                  </Link>
                )}
              </div>
            </div>
          )}

          {/* Google Signup Button */}
          <button
            type="button"
            onClick={handleGoogleSignup}
            disabled={googleLoading || isLoading || signupSuccess}
            className="w-full flex items-center justify-center gap-3 py-2.5 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-all text-sm font-medium text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer mb-5"
          >
            {googleLoading ? (
              <i className="ri-loader-4-line animate-spin text-gray-500 text-base"></i>
            ) : (
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.64 9.20455C17.64 8.56636 17.5827 7.95273 17.4764 7.36364H9V10.845H13.8436C13.635 11.97 13.0009 12.9232 12.0477 13.5614V15.8195H14.9564C16.6582 14.2527 17.64 11.9455 17.64 9.20455Z" fill="#4285F4"/>
                <path d="M9 18C11.43 18 13.4673 17.1941 14.9564 15.8195L12.0477 13.5614C11.2418 14.1014 10.2109 14.4205 9 14.4205C6.65591 14.4205 4.67182 12.8373 3.96409 10.71H0.957275V13.0418C2.43818 15.9832 5.48182 18 9 18Z" fill="#34A853"/>
                <path d="M3.96409 10.71C3.78409 10.17 3.68182 9.59318 3.68182 9C3.68182 8.40682 3.78409 7.83 3.96409 7.29V4.95818H0.957275C0.347727 6.17318 0 7.54773 0 9C0 10.4523 0.347727 11.8268 0.957275 13.0418L3.96409 10.71Z" fill="#FBBC05"/>
                <path d="M9 3.57955C10.3214 3.57955 11.5077 4.03364 12.4405 4.92545L15.0218 2.34409C13.4632 0.891818 11.4259 0 9 0C5.48182 0 2.43818 2.01682 0.957275 4.95818L3.96409 7.29C4.67182 5.16273 6.65591 3.57955 9 3.57955Z" fill="#EA4335"/>
              </svg>
            )}
            {googleLoading ? 'Connecting to Google...' : 'Continue with Google'}
          </button>

          {/* Divider */}
          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px bg-gray-200"></div>
            <span className="text-xs text-gray-400 font-medium">or sign up with email</span>
            <div className="flex-1 h-px bg-gray-200"></div>
          </div>

          {/* Signup Form */}
          <form onSubmit={handleSubmit} className="space-y-3.5 sm:space-y-4">
            {/* Full Name */}
            <div>
              <label htmlFor="fullName" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
                Full Name
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 sm:pl-3.5 flex items-center pointer-events-none">
                  <i className="ri-user-line text-gray-400 text-sm sm:text-base"></i>
                </div>
                <input
                  type="text"
                  id="fullName"
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className={`w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2.5 border rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all ${
                    errors.fullName ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="John Doe"
                />
              </div>
              {errors.fullName && (
                <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                  <i className="ri-error-warning-line"></i>
                  {errors.fullName}
                </p>
              )}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 sm:pl-3.5 flex items-center pointer-events-none">
                  <i className="ri-mail-line text-gray-400 text-sm sm:text-base"></i>
                </div>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={`w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2.5 border rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all ${
                    errors.email ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="you@example.com"
                />
              </div>
              {errors.email && (
                <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                  <i className="ri-error-warning-line"></i>
                  {errors.email}
                </p>
              )}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 sm:pl-3.5 flex items-center pointer-events-none">
                  <i className="ri-lock-line text-gray-400 text-sm sm:text-base"></i>
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className={`w-full pl-9 sm:pl-10 pr-10 sm:pr-11 py-2.5 border rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all ${
                    errors.password ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Create a strong password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 sm:pr-3.5 flex items-center cursor-pointer"
                >
                  <i
                    className={`${
                      showPassword ? 'ri-eye-off-line' : 'ri-eye-line'
                    } text-gray-400 hover:text-gray-600 transition-colors text-sm sm:text-base`}
                  ></i>
                </button>
              </div>
              {formData.password && (
                <div className="mt-2">
                  <div className="flex gap-1.5 mb-1">
                    {[1, 2, 3].map((bar) => (
                      <div
                        key={bar}
                        className={`h-1 flex-1 rounded-full transition-all ${
                          bar <= passwordStrength.level ? passwordStrength.color : 'bg-gray-200'
                        }`}
                      />
                    ))}
                  </div>
                  <p
                    className={`text-xs font-medium ${
                      passwordStrength.level === 1
                        ? 'text-red-500'
                        : passwordStrength.level === 2
                        ? 'text-amber-500'
                        : 'text-emerald-500'
                    }`}
                  >
                    {passwordStrength.label} password
                  </p>
                </div>
              )}
              {errors.password && (
                <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                  <i className="ri-error-warning-line"></i>
                  {errors.password}
                </p>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
                Confirm Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 sm:pl-3.5 flex items-center pointer-events-none">
                  <i className="ri-lock-2-line text-gray-400 text-sm sm:text-base"></i>
                </div>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  className={`w-full pl-9 sm:pl-10 pr-10 sm:pr-11 py-2.5 border rounded-lg text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all ${
                    errors.confirmPassword
                      ? 'border-red-300 bg-red-50'
                      : formData.confirmPassword && formData.password === formData.confirmPassword
                      ? 'border-emerald-300 bg-emerald-50/50'
                      : 'border-gray-300'
                  }`}
                  placeholder="Re-enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 sm:pr-3.5 flex items-center cursor-pointer"
                >
                  <i
                    className={`${
                      showConfirmPassword ? 'ri-eye-off-line' : 'ri-eye-line'
                    } text-gray-400 hover:text-gray-600 transition-colors text-sm sm:text-base`}
                  ></i>
                </button>
              </div>
              {formData.confirmPassword && formData.password === formData.confirmPassword && (
                <p className="mt-1.5 text-xs text-emerald-600 flex items-center gap-1">
                  <i className="ri-checkbox-circle-line"></i>
                  Passwords match
                </p>
              )}
              {errors.confirmPassword && (
                <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                  <i className="ri-error-warning-line"></i>
                  {errors.confirmPassword}
                </p>
              )}
            </div>

            {/* Terms Agreement */}
            <div className="pt-1 space-y-3">
              <label className="flex items-start gap-2.5 sm:gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-0.5 w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500 cursor-pointer shrink-0"
                />
                <span className="text-xs text-gray-500 leading-relaxed">
                  I agree to the{' '}
                  <a href="#terms" className="text-teal-600 font-medium hover:text-teal-700 underline" rel="nofollow">
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a href="#privacy" className="text-teal-600 font-medium hover:text-teal-700 underline" rel="nofollow">
                    Privacy Policy
                  </a>
                </span>
              </label>
              {errors.terms && (
                <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                  <i className="ri-error-warning-line"></i>
                  {errors.terms}
                </p>
              )}

              {/* Tax Responsibility Clause */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreedToTaxPolicy}
                    onChange={(e) => setAgreedToTaxPolicy(e.target.checked)}
                    className="mt-0.5 w-4 h-4 text-amber-600 border-amber-300 rounded focus:ring-amber-500 cursor-pointer shrink-0"
                  />
                  <span className="text-xs text-amber-800 leading-relaxed">
                    <strong>Tax Responsibility:</strong> I understand that as a seller or event organizer, I am solely responsible for determining, collecting, and remitting any applicable taxes (VAT, GST, sales tax, etc.) in my jurisdiction. EVENTSHOWCASE does not collect or remit taxes on my behalf.
                  </span>
                </label>
                {errors.taxPolicy && (
                  <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
                    <i className="ri-error-warning-line"></i>
                    {errors.taxPolicy}
                  </p>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading || signupSuccess}
              className="w-full py-2.5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg font-semibold hover:from-teal-600 hover:to-cyan-600 transition-all duration-300 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap text-xs sm:text-sm cursor-pointer"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  Creating account...
                </span>
              ) : signupSuccess ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-checkbox-circle-line"></i>
                  Account Created!
                </span>
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          {/* Login Link */}
          <p className="mt-6 sm:mt-8 text-center text-xs sm:text-sm text-gray-500">
            Already have an account?{' '}
            <Link to={redirectPath ? `/login?redirect=${encodeURIComponent(redirectPath)}` : '/login'} className="font-semibold text-teal-600 hover:text-teal-700 transition-colors">
              Log in
            </Link>
          </p>

          {/* Back to Home */}
          <div className="text-center mt-4 sm:mt-6">
            <Link
              to="/"
              className="text-xs text-gray-400 hover:text-gray-600 transition-colors inline-flex items-center gap-1.5"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
